/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable multiline-ternary */
import {
  CakeIcon,
  MapIcon,
  PhoneIcon,
  HashtagIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
  ScaleIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline'
import { type Nationality, type Patient } from '../../model/patient'
import { generatePatientFields } from './utils'
import { getPatientService } from '../../services/Services'
import { useCallback, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  deletePatient,
  updateCurrentPatient
} from '../../reducers/patientsReducer'
import {
  clearCurrentPatient,
  setCurrentPatient
} from '../../reducers/currentPatientReducer'
import { toCamelCase } from '../../utils/schemaUtils'
import { getAge, makeDate } from '../../utils/dateUtils'
import {
  selectCurrentVisits,
  getLatestVisitWithWeight
} from '../../reducers/currentVisitsReducer'
import { Form } from 'react-final-form'
import InputField from '../common/form/InputField/InputField'
import DateField from '../common/form/DateField/DateField'
import RadioField from '../common/form/RadioField/RadioField'
import TextareaField from '../common/form/TextareaField/TextareaField'
import FilePicker from '../common/FilePicker/FilePicker'
import {
  required,
  composeValidators,
  isValidDate
} from '../common/form/validators'
import { useIntl } from 'react-intl'
import messages from '../../client/modules/patient/components/PatientModal/components/PatientForm/messages'
import {
  Accordion,
  Text,
  AccordionButton,
  AccordionItem,
  AccordionIcon,
  AccordionPanel,
  Flex,
  Alert,
  AlertTitle,
  AlertDescription,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Box,
  Heading,
  List,
  ListItem,
  ListIcon,
  SimpleGrid,
  HStack,
  VStack,
  IconButton,
  useDisclosure
} from '@chakra-ui/react'
import { useAccount } from '../../client/modules/account/AccountProvider'
import { useNavigate, useParams } from 'react-router-dom'
import PatientModal from '../../client/modules/patient/components/PatientModal'
import { CountrySelectField } from '../common/form/CountrySelect'

interface Props {
  currentPatient: Patient
}

interface EditValues {
  name: string
  dateOfBirth: string
  sex: 'M' | 'F'
  nationality: Nationality
  address: string
  phoneNumber: string
  history: Record<string, string>
}

function patientToEditValues(patient: Patient): EditValues {
  return {
    name: patient.name || '',
    dateOfBirth: patient.dateOfBirth || '',
    sex: patient.sex || 'M',
    nationality: patient.nationality,
    address: patient.address || '',
    phoneNumber: patient.phoneNumber || '',
    history: patient.history || {}
  }
}

const GENDER_OPTIONS = [
  { title: 'Male', value: 'M' },
  { title: 'Female', value: 'F' }
]

export default function CurrentPatientSidebar({ currentPatient }: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const toast = useToast()
  const { formatMessage } = useIntl()

  const currentVisits = useAppSelector(selectCurrentVisits)
  const latestVisitWithWeight = useAppSelector(getLatestVisitWithWeight)

  const { patientId } = useParams()
  const { schema } = useAccount()

  const [isEditing, setIsEditing] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([])
  const {
    isOpen: isEditDrawerOpen,
    onOpen: onEditDrawerOpen,
    onClose: onEditDrawerClose
  } = useDisclosure()
  const patientInitials = (currentPatient?.name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')

  const handlePatientDelete = () => {
    if (!patientId) {
      return
    }
    getPatientService()
      .deletePatient(patientId)
      .then(() => {
        dispatch(deletePatient(patientId))
        dispatch(clearCurrentPatient())
        toast({
          title: 'Patient archived',
          status: 'success',
          duration: 4000,
          isClosable: true
        })
        navigate('/dashboard')
      })
      .catch((error) => toast({ title: error.message, status: 'error' }))
  }

  const handleInlineSave = (values: EditValues) => {
    getPatientService()
      .updatePatient({
        ...currentPatient,
        ...values,
        attachments: uploadedFiles,
        captures: []
      })
      .then((response) => {
        dispatch(updateCurrentPatient(response))
        dispatch(setCurrentPatient(response))
        setIsEditing(false)
        setUploadedFiles([])
        toast({
          title: 'Patient updated',
          status: 'success',
          duration: 4000,
          isClosable: true
        })
      })
  }

  const [images, setImages] = useState<string[]>([])

  const fetchAttachments = useCallback(async () => {
    const attachments = await getPatientService().getPatientAttachments(
      currentPatient.attachmentIds
    )
    setImages(attachments)
  }, [currentPatient])

  useEffect(() => {
    if (patientId) {
      fetchAttachments()
    }
  }, [patientId, fetchAttachments])

  if (!currentPatient || currentPatient.id === '') {
    return (
      <Box
        as="aside"
        position="relative"
        width="full"
        bg="white"
        p={6}
        minH={{ base: 'auto', lg: 'calc(100vh - 48px)' }}
      >
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="md" color="gray.400">
            No patient selected
          </Heading>
        </Flex>
      </Box>
    )
  }

  return (
    <Form
      onSubmit={handleInlineSave}
      initialValues={patientToEditValues(currentPatient)}
      render={({ handleSubmit }) => (
        <Box
          as="aside"
          position={{ base: 'relative', lg: 'sticky' }}
          top={{ base: 'auto', lg: '48px' }}
          width="full"
          height={{ base: 'auto', lg: 'calc(100vh - 48px)' }}
          bg="gray.50"
          p={{ base: 3, md: 4 }}
          overflowY={{ base: 'visible', lg: 'auto' }}
          transition="all 0.2s"
        >
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="12px"
            boxShadow="0 1px 2px rgba(15, 23, 42, 0.04)"
            p={{ base: 4, md: 5 }}
            mb={3}
          >
            <Flex justify="space-between" align="start" mb={5}>
              <Flex align="center" gap={3} minW={0} flex="1">
                <Flex
                  flex="0 0 auto"
                  align="center"
                  justify="center"
                  w="42px"
                  h="42px"
                  borderRadius="10px"
                  bg="gray.900"
                  color="white"
                  fontSize="sm"
                  fontWeight="700"
                  letterSpacing="0.04em"
                >
                  {patientInitials || 'P'}
                </Flex>
                <Box minW={0} flex="1">
                  <Text
                    mb={0.5}
                    color="gray.500"
                    fontSize="10px"
                    fontWeight="700"
                    letterSpacing="0.1em"
                    textTransform="uppercase"
                  >
                    Patient profile
                  </Text>
                  {isEditing ? (
                    <InputField
                      name="name"
                      label=""
                      validate={required}
                      showAsterisk={false}
                    />
                  ) : (
                    <Heading
                      size="md"
                      color="gray.900"
                      letterSpacing="tight"
                      noOfLines={2}
                    >
                      {currentPatient.name}
                    </Heading>
                  )}
                </Box>
              </Flex>
              <Menu placement="bottom-end">
                <MenuButton
                  as={IconButton}
                  variant="ghost"
                  aria-label="Patient options"
                  icon={
                    <EllipsisVerticalIcon
                      style={{ width: '20px', height: '20px' }}
                    />
                  }
                  color="gray.600"
                  borderRadius="8px"
                  size="sm"
                />
                <MenuList>
                  <MenuItem
                    onClick={onEditDrawerOpen}
                    icon={<PencilSquareIcon width="18px" />}
                  >
                    Edit Profile
                  </MenuItem>
                  <MenuItem
                    onClick={handlePatientDelete}
                    icon={<TrashIcon width="18px" />}
                    color="red.600"
                  >
                    Archive Patient
                  </MenuItem>
                </MenuList>
              </Menu>
            </Flex>

            {isEditing && (
              <HStack spacing={2} mb={5}>
                <Button
                  colorScheme="gray"
                  size="sm"
                  onClick={() => {
                    void handleSubmit()
                  }}
                >
                  Save Changes
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsEditing(false)
                    setUploadedFiles([])
                  }}
                >
                  Cancel
                </Button>
              </HStack>
            )}
            <VStack align="stretch" spacing={0}>
              <List
                spacing={0}
                borderTop="1px solid"
                borderColor="gray.100"
                sx={{
                  '& > li': {
                    paddingBlock: '10px',
                    borderBottom: '1px solid',
                    borderColor: 'var(--chakra-colors-gray-100)'
                  },
                  '& > li:last-of-type': { borderBottom: 0, paddingBottom: 0 }
                }}
              >
                <ListItem display="flex" alignItems="center" color="gray.600">
                  <ListIcon
                    as={HashtagIcon}
                    boxSize={8}
                    p={1.5}
                    bg="gray.100"
                    color="gray.700"
                    borderRadius="8px"
                  />
                  <Box>
                    <Text
                      color="gray.500"
                      fontSize="10px"
                      fontWeight="700"
                      letterSpacing="0.08em"
                      textTransform="uppercase"
                    >
                      Patient ID
                    </Text>
                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                      {patientId?.slice(0, 6)}
                    </Text>
                  </Box>
                </ListItem>

                <ListItem
                  display="flex"
                  alignItems="flex-start"
                  color="gray.600"
                >
                  <ListIcon
                    as={CakeIcon}
                    boxSize={8}
                    p={1.5}
                    bg="gray.100"
                    color="gray.700"
                    borderRadius="8px"
                  />
                  {isEditing ? (
                    <VStack align="stretch" flex={1} spacing={3}>
                      <DateField
                        name="dateOfBirth"
                        label={formatMessage(messages.dateOfBirth)}
                        validate={composeValidators(required, isValidDate)}
                        outputFormat="DD.MM.YYYY"
                      />
                      <RadioField
                        name="sex"
                        label={formatMessage(messages.sex)}
                        options={GENDER_OPTIONS}
                        validate={required}
                      />
                    </VStack>
                  ) : (
                    <Box>
                      <Text
                        color="gray.500"
                        fontSize="10px"
                        fontWeight="700"
                        letterSpacing="0.08em"
                        textTransform="uppercase"
                      >
                        Date of birth
                      </Text>
                      <Text fontSize="sm" fontWeight="600" color="gray.800">
                        {currentPatient.dateOfBirth}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {`${getAge(currentPatient.dateOfBirth)} · ${
                          currentPatient.sex === 'M' ? 'Male' : 'Female'
                        }`}
                      </Text>
                    </Box>
                  )}
                </ListItem>

                {latestVisitWithWeight && (
                  <ListItem display="flex" alignItems="center" color="gray.600">
                    <ListIcon
                      as={ScaleIcon}
                      boxSize={8}
                      p={1.5}
                      bg="gray.100"
                      color="gray.700"
                      borderRadius="8px"
                    />
                    <Box>
                      <Text
                        color="gray.500"
                        fontSize="10px"
                        fontWeight="700"
                        letterSpacing="0.08em"
                        textTransform="uppercase"
                      >
                        Last weight
                      </Text>
                      <Text fontSize="sm" fontWeight="600" color="gray.800">
                        {latestVisitWithWeight.weight
                          ? `${latestVisitWithWeight.weight} KG`
                          : 'NA'}
                      </Text>
                    </Box>
                  </ListItem>
                )}

                {(isEditing || currentPatient.address) && (
                  <ListItem display="flex" alignItems="start" color="gray.600">
                    <ListIcon
                      as={MapIcon}
                      boxSize={8}
                      p={1.5}
                      bg="gray.100"
                      color="gray.700"
                      borderRadius="8px"
                    />
                    <Box flex="1">
                      {isEditing ? (
                        <InputField
                          name="address"
                          label={formatMessage(messages.address)}
                        />
                      ) : (
                        <>
                          <Text
                            color="gray.500"
                            fontSize="10px"
                            fontWeight="700"
                            letterSpacing="0.08em"
                            textTransform="uppercase"
                          >
                            Address
                          </Text>
                          <Text fontSize="sm" fontWeight="600" color="gray.800">
                            {currentPatient.address}
                          </Text>
                        </>
                      )}
                    </Box>
                  </ListItem>
                )}

                {(isEditing || currentPatient.phoneNumber) && (
                  <ListItem display="flex" alignItems="start" color="gray.600">
                    <ListIcon
                      as={PhoneIcon}
                      boxSize={8}
                      p={1.5}
                      bg="gray.100"
                      color="gray.700"
                      borderRadius="8px"
                    />
                    <Box flex="1">
                      {isEditing ? (
                        <InputField
                          name="phoneNumber"
                          label={formatMessage(messages.phoneNumber)}
                        />
                      ) : (
                        <>
                          <Text
                            color="gray.500"
                            fontSize="10px"
                            fontWeight="700"
                            letterSpacing="0.08em"
                            textTransform="uppercase"
                          >
                            Phone
                          </Text>
                          <Text fontSize="sm" fontWeight="600" color="gray.800">
                            {currentPatient.phoneNumber}
                          </Text>
                        </>
                      )}
                    </Box>
                  </ListItem>
                )}

                {isEditing && (
                  <ListItem display="flex" alignItems="start" color="gray.600">
                    <ListIcon
                      as={HashtagIcon}
                      boxSize={8}
                      p={1.5}
                      bg="gray.100"
                      color="gray.700"
                      borderRadius="8px"
                    />
                    <Box flex="1">
                      <CountrySelectField
                        name="nationality"
                        label={formatMessage(messages.nationality)}
                      />
                    </Box>
                  </ListItem>
                )}

                <ListItem display="flex" alignItems="center" color="gray.600">
                  <ListIcon
                    as={CalendarDaysIcon}
                    boxSize={8}
                    p={1.5}
                    bg="gray.100"
                    color="gray.700"
                    borderRadius="8px"
                  />
                  <Box>
                    <Text
                      color="gray.500"
                      fontSize="10px"
                      fontWeight="700"
                      letterSpacing="0.08em"
                      textTransform="uppercase"
                    >
                      Visits
                    </Text>
                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                      {`${currentVisits.length} visit${
                        currentVisits.length === 1 ? '' : 's'
                      }`}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Since {makeDate(currentPatient.createdAt)}
                    </Text>
                  </Box>
                </ListItem>
              </List>
            </VStack>
          </Box>

          {!isEditing && (
            <VStack spacing={2} align="stretch" mb={3}>
              {currentPatient.history &&
                schema.history.fields
                  .filter((field) => field.isHighlighted)
                  .map((field) => {
                    if (
                      !currentPatient.history[toCamelCase(field.displayName)]
                    ) {
                      return null
                    }
                    return (
                      <Alert
                        key={field.id}
                        status="info"
                        variant="subtle"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="10px"
                        bg="white"
                        px={4}
                        py={3}
                      >
                        <Box flex="1">
                          <AlertTitle
                            fontSize="xs"
                            color="gray.900"
                            letterSpacing="0.02em"
                          >
                            {field.displayName}
                          </AlertTitle>
                          <AlertDescription
                            fontSize="sm"
                            color="gray.700"
                            mt={1}
                          >
                            {
                              currentPatient.history[
                                toCamelCase(field.displayName)
                              ]
                            }
                          </AlertDescription>
                        </Box>
                      </Alert>
                    )
                  })}
            </VStack>
          )}

          <Box>
            {isEditing ? (
              <Accordion allowToggle defaultIndex={[0]}>
                <AccordionItem
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="10px"
                  overflow="hidden"
                >
                  <AccordionButton _hover={{ bg: 'gray.50' }} px={4} py={3}>
                    <Box as="span" flex="1" textAlign="start">
                      <Heading size="sm" fontWeight="bold" color="gray.700">
                        Edit {schema.history.displayName}
                      </Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel px={4} pt={2} pb={4}>
                    <VStack spacing={5} align="stretch">
                      {schema.history.fields.map((field: any) => {
                        const key = toCamelCase(field.displayName)
                        return (
                          <TextareaField
                            key={field.id}
                            name={`history.${key}`}
                            label={field.displayName}
                            description={field.description}
                          />
                        )
                      })}
                    </VStack>
                    <Box pt={6}>
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color="gray.600"
                        mb={3}
                      >
                        Add Attachments
                      </Text>
                      <FilePicker
                        onChange={(files) => {
                          setUploadedFiles(files)
                        }}
                        multiple
                      />
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            ) : (
              currentPatient.history &&
              Object.values(currentPatient.history).some(
                (val) => val !== null && val !== ''
              ) && (
                <Accordion allowToggle defaultIndex={[0]}>
                  <AccordionItem
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="10px"
                    overflow="hidden"
                  >
                    <AccordionButton px={4} py={3} _hover={{ bg: 'gray.50' }}>
                      <Box as="span" flex="1" textAlign="start">
                        <Heading size="sm" fontWeight="bold" color="gray.700">
                          Medical History
                        </Heading>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel pb={6}>
                      <VStack align="stretch" spacing={4}>
                        {schema.history.fields.map((field) =>
                          generatePatientFields(field, currentPatient.history)
                        )}
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              )
            )}
          </Box>

          {images.length > 0 && (
            <Box
              mt={3}
              p={4}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="10px"
            >
              <Heading size="sm" mb={4} color="gray.800">
                Attachments
              </Heading>
              <SimpleGrid columns={3} spacing={3}>
                {images.map((image) => (
                  <Box
                    as="a"
                    href={image}
                    target="_blank"
                    key={image}
                    rel="noreferrer"
                    overflow="hidden"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="gray.200"
                    transition="transform 0.2s"
                    _hover={{ transform: 'scale(1.05)' }}
                  >
                    <img
                      src={image}
                      alt="attachment"
                      style={{ width: '100%', height: 'auto' }}
                    />
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          )}

          <PatientModal
            historySchema={schema.history}
            isOpen={isEditDrawerOpen}
            isEditMode
            currentPatient={currentPatient}
            onClose={onEditDrawerClose}
          />
        </Box>
      )}
    />
  )
}
